import React, { useState, useEffect, useRef } from 'react'
import { dk, isToday, fmtDate, fmtDow, TODAY, addDays } from '../lib/dateUtils'

const COL_W = 72   // fixed px width per day column
const PROJ_W = 220 // fixed px width for job column

// US federal holidays. Returns true if the given Date falls on one.
function isHoliday(date) {
  const m = date.getMonth() // 0-indexed (0=Jan ... 11=Dec)
  const day = date.getDate()
  const dow = date.getDay() // 0=Sun, 1=Mon, ... 4=Thu

  // Fixed-date holidays
  if (m === 0 && day === 1) return true    // New Year's Day — Jan 1
  if (m === 6 && day === 4) return true     // Independence Day — Jul 4
  if (m === 10 && day === 11) return true   // Veterans Day — Nov 11
  if (m === 11 && day === 25) return true   // Christmas Day — Dec 25

  // Floating holidays (nth weekday of month)
  if (m === 1 && dow === 1 && day >= 15 && day <= 21) return true  // Presidents Day — 3rd Mon of Feb
  if (m === 4 && dow === 1 && day >= 25) return true               // Memorial Day — last Mon of May
  if (m === 8 && dow === 1 && day <= 7) return true                // Labor Day — 1st Mon of Sep
  if (m === 10 && dow === 4 && day >= 22 && day <= 28) return true // Thanksgiving — 4th Thu of Nov

  return false
}

export default function Board({ cellData, roster, role, activeJobs, requests, jobOrder, onJobOrderChange, onOpenCell, onMoveCell, todayTotal, tomTotal, outToday, pendingCount, rosterCount }) {
  // Generate 90 days: 30 back + 59 forward
  const ALL_DAYS = React.useMemo(() => {
    const days = []
    for (let i = -30; i <= 59; i++) {
      days.push(addDays(TODAY, i))
    }
    return days
  }, [])

  // Payroll-link flag (Phase 3): a board assignment whose worker_id is null or
  // doesn't resolve to a roster employee is "not linked to payroll". Resolve the
  // employee id set ONCE per render (Set membership — no per-cell lookups, no
  // fuzzy). Union/trade placeholder slots ("[Local 4]") are deliberate, not flagged.
  // All employee ids (incl. inactive — an inactive employee still has a real
  // payroll id; only a null/orphaned worker_id should flag).
  const rosterIdSet = React.useMemo(
    () => new Set((roster || []).filter(e => e && e.id != null).map(e => e.id)),
    [roster]
  )
  const isPlaceholderName = (n) => typeof n === 'string' && n.startsWith('[') && n.endsWith(']')
  const cellUnlinkedCount = (c) => {
    const ws = Array.isArray(c.workers) ? c.workers : []
    const ids = Array.isArray(c.worker_ids) ? c.worker_ids : []
    let n = 0
    ws.forEach((w, i) => { if (!isPlaceholderName(w) && (ids[i] == null || !rosterIdSet.has(ids[i]))) n++ })
    return n
  }

  const [search, setSearch] = useState('')
  const [dragIdx, setDragIdx] = useState(null)
  const [dragOverIdx, setDragOverIdx] = useState(null)
  // Cell drag state: dragging assignment from one day to another
  const [cellDrag, setCellDrag] = useState(null) // { jobId, dateKey, data }
  const [cellDragOver, setCellDragOver] = useState(null) // { jobId, dateKey }
  const [localOrder, setLocalOrder] = useState(null)
  const scrollRef = useRef(null)

  const canEdit = role === 'owner' || role === 'road_super'
  const canReorder = role === 'owner' || role === 'road_super'

  // Scroll to 2 days before today on mount
  useEffect(() => {
    if (scrollRef.current) {
      // Column 28 = 30 days back + 0 offset to show -2 days at left edge
      const scrollTo = (30 - 2) * COL_W
      scrollRef.current.scrollLeft = scrollTo
    }
  }, [])

  // Only sync from jobOrder on initial load (when localOrder is still null)
  useEffect(() => {
    if (localOrder === null && jobOrder && Array.isArray(jobOrder) && jobOrder.length > 0) {
      setLocalOrder(jobOrder)
    }
  }, [jobOrder, localOrder])

  const effectiveOrder = (localOrder && localOrder.length > 0) ? localOrder
    : (jobOrder && jobOrder.length > 0) ? jobOrder : null

  const orderedJobs = effectiveOrder
    ? effectiveOrder.map(id => activeJobs.find(j => j.id === id)).filter(Boolean)
        .concat(activeJobs.filter(j => !effectiveOrder.includes(j.id)))
    : activeJobs

  const filteredJobs = search.trim()
    ? orderedJobs.filter(j =>
        (j.name || '').toLowerCase().includes(search.toLowerCase()) ||
        String(j.num || '').toLowerCase().includes(search.toLowerCase()) ||
        (j.pm || '').toLowerCase().includes(search.toLowerCase()) ||
        (j.pmName || '').toLowerCase().includes(search.toLowerCase()) ||
        (j.gc || '').toLowerCase().includes(search.toLowerCase()) ||
        (j.address || '').toLowerCase().includes(search.toLowerCase()))
    : orderedJobs

  const pendingRequestKeys = new Set(
    (requests || []).filter(r => r.status === 'pending').map(r => `${r.jobId}-${r.dateKey}`)
  )

  function getCellHighlight(j, k, c) {
    const req = pendingRequestKeys.has(`${j.id}-${k}`)
    const mis = c.count > 0 && c.workers.length !== c.count
    if (req && mis) return '#FFD966'
    if (req) return '#FFF3B0'
    if (mis) return '#FFE4A0'
    return null
  }

  const handleDragStart = (e, idx) => { setDragIdx(idx); e.dataTransfer.effectAllowed = 'move' }
  const handleDragOver = (e, idx) => { e.preventDefault(); setDragOverIdx(idx) }
  const handleDrop = (e, idx) => {
    e.preventDefault()
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return }
    // Use the currently displayed job IDs (filteredJobs) as the base for reordering
    const currentIds = filteredJobs.map(j => j.id)
    const r = [...currentIds]
    const [moved] = r.splice(dragIdx, 1)
    r.splice(idx, 0, moved)
    console.log('Reordering jobs:', dragIdx, '->', idx, r)
    setLocalOrder(r)
    if (typeof onJobOrderChange === 'function') onJobOrderChange(r)
    setDragIdx(null); setDragOverIdx(null)
  }
  const handleDragEnd = () => { setDragIdx(null); setDragOverIdx(null) }
  const handleCellDragStart = (e, jobId, dateKey, data) => {
    if (!canEdit) return
    e.stopPropagation() // prevent row reorder drag
    setCellDrag({ jobId, dateKey, data })
    e.dataTransfer.effectAllowed = 'move'
    e.dataTransfer.setData('cellDrag', '1') // flag to identify cell drag
  }
  const handleCellDragOver = (e, jobId, dateKey) => {
    if (!cellDrag || cellDrag.jobId !== jobId) return // only same job
    e.preventDefault()
    e.stopPropagation()
    setCellDragOver({ jobId, dateKey })
  }
  const handleCellDrop = (e, jobId, dateKey) => {
    e.preventDefault()
    e.stopPropagation()
    if (!cellDrag || cellDrag.jobId !== jobId || cellDrag.dateKey === dateKey) {
      setCellDrag(null); setCellDragOver(null); return
    }
    // Wrong-day guard: a move touches BOTH days, so either end being today/past needs a confirm.
    const describe = (k, side) => {
      const dd = new Date(k)
      const lbl = dd.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })
      if (isToday(dd)) return `${side} day is TODAY (${lbl})`
      if (dd < TODAY) return `${side} day is a PAST day (${lbl})`
      return null
    }
    const flagged = [describe(cellDrag.dateKey, 'The source'), describe(dateKey, 'The destination')].filter(Boolean)
    if (flagged.length && !window.confirm(`${flagged.join(', and ')}. This move changes the live schedule — continue?`)) {
      setCellDrag(null); setCellDragOver(null); return
    }
    // Directly save to new day and clear source — no modal
    if (typeof onMoveCell === 'function') {
      onMoveCell(jobId, cellDrag.dateKey, dateKey, cellDrag.data)
    }
    setCellDrag(null); setCellDragOver(null)
  }

  const thBase = {
    position: 'sticky',
    top: 0,
    zIndex: 3,
    padding: '5px 4px',
    textAlign: 'center',
    fontSize: 10,
    fontWeight: 500,
    color: 'var(--text2)',
    whiteSpace: 'nowrap',
    userSelect: 'none',
    background: 'var(--surface2)',
    borderRight: '0.5px solid var(--border)',
    borderBottom: '0.5px solid var(--border)',
    width: COL_W,
    minWidth: COL_W,
    maxWidth: COL_W,
    boxSizing: 'border-box',
  }

  const TABLE_W = PROJ_W + ALL_DAYS.length * COL_W

  return (
    <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0, overflow: 'hidden' }}>

      {/* Filter / stats bar */}
      <div style={{ padding: '6px 10px', background: 'var(--surface)', borderBottom: '0.5px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        <input className="form-input" style={{ width: 200, minWidth: 140, fontSize: 12 }} value={search}
          onChange={e => setSearch(e.target.value)} placeholder="Filter jobs..." />
        {search && <button className="btn btn-sm" onClick={() => setSearch('')} style={{ fontSize: 11 }}>Clear</button>}
        <div style={{ display: 'flex', gap: 5, alignItems: 'center', borderLeft: '0.5px solid var(--border)', paddingLeft: 8 }}>
          {[{ val: todayTotal, lbl: 'Today' }, { val: tomTotal, lbl: 'Tomorrow' }, { val: activeJobs.length, lbl: 'Jobs' }, { val: rosterCount, lbl: 'Roster' }].map(({ val, lbl }) => (
            <div key={lbl} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--surface2)', border: '0.5px solid var(--border)', borderRadius: 7, padding: '3px 9px', minWidth: 44 }}>
              <span style={{ fontWeight: 700, fontSize: 13, color: 'var(--text)', lineHeight: 1.2 }}>{val}</span>
              <span style={{ color: 'var(--text3)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.3px' }}>{lbl}</span>
            </div>
          ))}
          {outToday > 0 && <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--red-bg)', border: '0.5px solid #F09595', borderRadius: 7, padding: '3px 9px', minWidth: 44 }}><span style={{ fontWeight: 700, fontSize: 13, color: 'var(--red-text)', lineHeight: 1.2 }}>{outToday}</span><span style={{ color: 'var(--red-text)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.3px' }}>Out</span></div>}
          {pendingCount > 0 && <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', background: 'var(--amber-bg)', border: '0.5px solid #D4A800', borderRadius: 7, padding: '3px 9px', minWidth: 44 }}><span style={{ fontWeight: 700, fontSize: 13, color: 'var(--amber-text)', lineHeight: 1.2 }}>{pendingCount}</span><span style={{ color: 'var(--amber-text)', fontSize: 9, textTransform: 'uppercase', letterSpacing: '.3px' }}>Pending</span></div>}
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text3)' }}><span style={{ width: 10, height: 10, background: '#FFF3B0', border: '0.5px solid #D4A800', borderRadius: 2, display: 'inline-block' }} />Pending</span>
          <span style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: 'var(--text3)' }}><span style={{ width: 10, height: 10, background: '#FFE4A0', border: '0.5px solid #C47B00', borderRadius: 2, display: 'inline-block' }} />Count≠names</span>
          {!search && canReorder && <span style={{ fontSize: 10, color: 'var(--text3)' }}>⠿ Reorder</span>}

          <button className="btn btn-sm" style={{ fontSize: 10 }} onClick={() => { if (scrollRef.current) scrollRef.current.scrollLeft = (30 - 2) * COL_W }}>Today</button>
        </div>
      </div>

      {/* Scrollable table */}
      <div ref={scrollRef} style={{ flex: 1, overflow: 'auto', WebkitOverflowScrolling: 'touch' }}>
        <table style={{ borderCollapse: 'collapse', tableLayout: 'fixed', width: TABLE_W, minWidth: TABLE_W }}>
          <colgroup>
            <col style={{ width: PROJ_W, minWidth: PROJ_W }} />
            {ALL_DAYS.map(d => <col key={dk(d)} style={{ width: COL_W, minWidth: COL_W }} />)}
          </colgroup>

          <thead>
            {/* Day headers */}
            <tr>
              <th style={{ ...thBase, width: PROJ_W, minWidth: PROJ_W, textAlign: 'left', paddingLeft: 8, position: 'sticky', top: 0, left: 0, zIndex: 6, background: 'var(--surface2)' }}>Job</th>
              {ALL_DAYS.map(d => {
                const isWE = d.getDay() === 0 || d.getDay() === 6
                const isPast = d < TODAY && !isToday(d)
                const isHol = isHoliday(d)
                return (
                  <th key={dk(d)} title={isHol ? 'Holiday' : undefined} style={{ ...thBase,
                    background: isToday(d) ? 'var(--blue-bg)' : isHol ? '#FEE2E2' : isWE ? '#F0F0ED' : isPast ? '#F8F8F8' : 'var(--surface2)',
                    color: isToday(d) ? 'var(--blue-text)' : isWE || isPast ? 'var(--text3)' : undefined,
                    opacity: isPast ? 0.75 : 1 }}>
                    {fmtDow(d)}<br />
                    <span style={{ fontSize: 9, fontWeight: 400 }}>{fmtDate(d)}</span>
                    {isWE && <><br /><span style={{ fontSize: 8, opacity: .6 }}>wknd</span></>}
                  </th>
                )
              })}
            </tr>
            {/* Daily totals */}
            <tr>
              <th style={{ ...thBase, top: 44, width: PROJ_W, minWidth: PROJ_W, textAlign: 'left', paddingLeft: 8, position: 'sticky', top: 44, left: 0, zIndex: 6, fontSize: 10, fontWeight: 600, background: 'var(--surface2)' }}>Daily total</th>
              {ALL_DAYS.map(d => {
                const k = dk(d)
                const dt = filteredJobs.reduce((s, j) => s + (cellData[j.id]?.[k]?.count || 0), 0)
                const isWE = d.getDay() === 0 || d.getDay() === 6
                const isPast = d < TODAY && !isToday(d)
                const isHol = isHoliday(d)
                return (
                  <th key={k} title={isHol ? 'Holiday' : undefined} style={{ ...thBase, top: 44, fontWeight: 700, fontSize: 13,
                    color: dt > 0 ? 'var(--text)' : 'var(--text3)',
                    background: isToday(d) ? '#E0EDFA' : isHol ? '#FEE2E2' : isWE ? '#EAEAE7' : isPast ? '#F5F5F5' : 'var(--surface2)',
                    opacity: isPast && dt === 0 ? 0.6 : 1 }}>
                    {dt > 0 ? dt : '—'}
                  </th>
                )
              })}
            </tr>
          </thead>

          <tbody>
            {filteredJobs.length === 0 ? (
              <tr><td colSpan={ALL_DAYS.length + 1} style={{ padding: 32, textAlign: 'center', fontSize: 13, color: 'var(--text3)' }}>No jobs match "{search}"</td></tr>
            ) : filteredJobs.map((j, idx) => (
              <tr key={j.id}
                draggable={!search && canReorder}
                onDragStart={e => handleDragStart(e, idx)}
                onDragOver={e => handleDragOver(e, idx)}
                onDrop={e => handleDrop(e, idx)}
                onDragEnd={handleDragEnd}
                style={{ opacity: dragIdx === idx ? 0.4 : 1, borderTop: dragOverIdx === idx && dragIdx !== idx ? '2px solid var(--blue)' : undefined }}>
                {/* Sticky job column */}
                <td style={{ position: 'sticky', left: 0, zIndex: 2, background: 'var(--surface)', borderBottom: '0.5px solid var(--border)', borderRight: '0.5px solid var(--border)', padding: '5px 7px', cursor: canEdit ? 'pointer' : 'default', verticalAlign: 'middle', width: PROJ_W, minWidth: PROJ_W }}

                  onDoubleClick={canReorder ? (e) => {
                    e.stopPropagation()
                    const ids = filteredJobs.map(j => j.id)
                    const r = [ids[idx], ...ids.filter((_, i) => i !== idx)]
                    setLocalOrder(r)
                    if (typeof onJobOrderChange === 'function') onJobOrderChange(r)
                  } : undefined}
                  title={canReorder ? 'Double-click to move to top' : undefined}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                    {!search && canReorder && <span style={{ fontSize: 14, color: 'var(--text3)', cursor: 'grab', flexShrink: 0, userSelect: 'none' }}>⠿</span>}
                    <div style={{ width: 3, height: 28, borderRadius: 2, background: j.color, flexShrink: 0 }} />
                    <div style={{ minWidth: 0 }}>
                      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }} title={j.num + ' ' + j.name}>{j.num} {j.name}</div>
                      <div style={{ fontSize: 9, color: 'var(--text3)' }}>{j.pm}</div>
                    </div>
                  </div>
                </td>
                {ALL_DAYS.map(d => {
                  const k = dk(d)
                  const c = cellData[j.id]?.[k] || { count: 0, foreman: j.foreman || '', task: '', workers: [] }
                  const hlBg = getCellHighlight(j, k, c)
                  const isPast = d < TODAY && !isToday(d)
                  const isHol = isHoliday(d)
                  const isCellDragOver = cellDragOver?.jobId === j.id && cellDragOver?.dateKey === k
                  const isCellDragging = cellDrag?.jobId === j.id && cellDrag?.dateKey === k
                  const bg = isCellDragOver ? '#D1FAE5' : hlBg || (isToday(d) ? '#F0F6FF' : isHol ? '#FEE2E2' : isPast ? '#FAFAFA' : undefined)
                  const hasData = c.count > 0 || c.foreman || c.task
                  const unlinked = c.count > 0 ? cellUnlinkedCount(c) : 0
                  return (
                    <td key={k}
                      style={{ background: bg, borderBottom: '0.5px solid var(--border)', borderRight: isCellDragOver ? '2px solid #10B981' : '0.5px solid var(--border)', verticalAlign: 'top', padding: 0, opacity: isCellDragging ? 0.4 : isPast && !c.count ? 0.5 : 1 }}
                      draggable={canEdit && hasData}
                      onDragStart={canEdit && hasData ? (e) => handleCellDragStart(e, j.id, k, c) : undefined}
                      onDragOver={(e) => handleCellDragOver(e, j.id, k)}
                      onDragLeave={() => setCellDragOver(null)}
                      onDrop={(e) => handleCellDrop(e, j.id, k)}
                    >
                      <div className="cell" onClick={() => canEdit && onOpenCell({ jobId: j.id, dateKey: k })}>
                        {c.count > 0 ? <>
                          <div className="cell-top">
                            <span className="cell-count">{c.count}</span>
                            <span className="cell-fm">{c.foreman ? c.foreman.split(' ').pop() : ''}</span>
                            {unlinked > 0 && (
                              <span
                                title={`${unlinked} worker(s) not linked to payroll — open the cell to re-pick them from the roster`}
                                style={{ marginLeft: 'auto', width: 7, height: 7, borderRadius: '50%', background: '#e8820c', flexShrink: 0, alignSelf: 'center' }}
                              />
                            )}
                          </div>
                          {c.task && <div className="cell-task">{c.task}</div>}
                          <div className="cell-chips">
                            {c.workers.slice(0, 1).map(w => <span key={w} className="chip">{w.split(' ')[0]}</span>)}
                            {c.workers.length > 1 && <span className="chip">+{c.workers.length - 1}</span>}
                          </div>
                        </> : c.task ? (
                          <div className="cell-task" style={{ padding: '4px 2px' }}>{c.task}</div>
                        ) : <div className="empty-cell">—</div>}
                      </div>
                    </td>
                  )
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}
